import { create } from 'zustand';
import { DailySunlightResult } from '@/types/sunlight';
import { GridPoint } from '@/utils/gridSelection';

interface DailySunlightStore {
	startDate: Date;
	setStartDate: (date: Date) => void;
	endDate: Date;
	setEndDate: (date: Date) => void;
	gridSize: number;
	setGridSize: (size: number) => void;
	useApproximation: boolean;
	setUseApproximation: (use: boolean) => void;
	currentStatusMessage: string;
	setCurrentStatusMessage: (message: string) => void;
	hoveredGridPointData: DailySunlightResult | null;
	detailedGridPointsData: DailySunlightResult[];
	setDetailedGridPointsData: (data: DailySunlightResult[]) => void;
	setHoveredGridPointData: (data: DailySunlightResult | null) => void;
	sunlightTabIndex: number;
	setSunlightTabIndex: (index: number) => void;
	// Facade tooltip data
	hoveredFacadeData: DailySunlightResult | null;
	setHoveredFacadeData: (data: DailySunlightResult | null) => void;
	facadeWallData: DailySunlightResult[];
	setFacadeWallData: (data: DailySunlightResult[]) => void;
	facadeTabIndex: number;
	setFacadeTabIndex: (index: number) => void;
	currentSelectedMetric: keyof DailySunlightResult;
	setCurrentSelectedMetric: (metric: keyof DailySunlightResult) => void;
	sunlightResultsRooms: DailySunlightResult[] | null;
	setSunlightResultsRooms: (results: DailySunlightResult[] | null) => void;
	sunlightResultsGridPoints: DailySunlightResult[][] | null;
	setSunlightResultsGridPoints: (results: DailySunlightResult[] | null) => void;
	// Facade animation state
	isAnimatingFacade: boolean;
	setIsAnimatingFacade: (isAnimating: boolean) => void;
	currentAnimationDay: number;
	setCurrentAnimationDay: (day: number) => void;
	// Daily sunlight animation state
	isAnimatingDaily: boolean;
	setIsAnimatingDaily: (isAnimating: boolean) => void;
	currentDailyAnimationDay: number;
	setCurrentDailyAnimationDay: (day: number) => void;
	selectedGridPoints: GridPoint[];
	setSelectedGridPoints: (points: GridPoint[]) => void;
}

export const useDailySunlightStore = create<DailySunlightStore>((set) => {
	const currentYear = new Date().getFullYear();
	const startOfYear = new Date(currentYear, 0, 1);
	const endOfYear = new Date(currentYear, 11, 31);

	return {
		startDate: startOfYear,
		setStartDate: (date) => set(() => ({ startDate: date })),
		endDate: endOfYear,
		setEndDate: (date) => set(() => ({ endDate: date })),
		gridSize: 1,
		setGridSize: (size) => set(() => ({ gridSize: size })),
		useApproximation: true,
		setUseApproximation: (use) => set(() => ({ useApproximation: use })),
		currentStatusMessage: '',
		setCurrentStatusMessage: (message) => set(() => ({ currentStatusMessage: message })),
		hoveredGridPointData: null,
		setHoveredGridPointData: (data) => set(() => ({ hoveredGridPointData: data })),
		detailedGridPointsData: [] as DailySunlightResult[],
		setDetailedGridPointsData: (data) => set(() => ({ detailedGridPointsData: data })),
		sunlightTabIndex: 0,
		setSunlightTabIndex: (index) => set(() => ({ sunlightTabIndex: index })),
		// Facade tooltip data
		hoveredFacadeData: null,
		setHoveredFacadeData: (data) => set(() => ({ hoveredFacadeData: data })),
		facadeWallData: [] as DailySunlightResult[],
		setFacadeWallData: (data) => set(() => ({ facadeWallData: data })),
		facadeTabIndex: 0,
		setFacadeTabIndex: (index) => set(() => ({ facadeTabIndex: index })),
		currentSelectedMetric: 'averageDailySunlightHours',
		setCurrentSelectedMetric: (metric) => set(() => ({ currentSelectedMetric: metric })),
		sunlightResultsRooms: null,
		setSunlightResultsRooms: (results) => set(() => ({ sunlightResultsRooms: results })),
		sunlightResultsGridPoints: null,
		setSunlightResultsGridPoints: (results) =>
			set((state) => {
				if (!results) return { sunlightResultsGridPoints: null };
				return {
					sunlightResultsGridPoints: [
						...(state.sunlightResultsGridPoints || []),
						results,
					],
				};
			}),
		// Facade animation state
		isAnimatingFacade: false,
		setIsAnimatingFacade: (isAnimating) => set(() => ({ isAnimatingFacade: isAnimating })),
		currentAnimationDay: 0,
		setCurrentAnimationDay: (day) => set(() => ({ currentAnimationDay: day })),
		// Daily sunlight animation state
		isAnimatingDaily: false,
		setIsAnimatingDaily: (isAnimating) => set(() => ({ isAnimatingDaily: isAnimating })),
		currentDailyAnimationDay: 0,
		setCurrentDailyAnimationDay: (day) => set(() => ({ currentDailyAnimationDay: day })),
		selectedGridPoints: [] as GridPoint[],
		setSelectedGridPoints: (points) => set(() => ({ selectedGridPoints: points })),
	};
});
